import { Box, Divider, FlexBox } from '@dixa/lib-shared-habla';
import { SearchResult } from 'lib-search-types';
import { MetaConversation } from 'lib-shared-legacy-types';
import type { Conversation } from 'lib-shared-types-api';

import { ConversationStatusColorBox } from 'components/conversation';

import { ConversationPreviewActions } from './components/actions/conversationPreviewActions';
import { ConversationPreviewCustomFields } from './components/customFields/conversationPreviewCustomFields';
import { ConversationPreviewCustomer } from './components/customer/conversationPreviewCustomer';
import { ConversationPreviewHeadline } from './components/headline/conversationPreviewHeadline';
import { ConversationPreviewTags } from './components/tags/conversationPreviewTags';
import styles from './conversationPreviewHeader.module.scss';

type ConversationPreviewHeaderProps = {
  csid: number;
  metaConversation: MetaConversation;
  highlights: SearchResult<'conversations'>['highlights'];
  onPreviewClose: () => void;
  onGoToConversation: () => void;
  /** Whether to show the go-to/close actions. Defaults to true. */
  showActions?: boolean;
  /** Whether to show tags and custom fields. Defaults to true. */
  showDetails?: boolean;
  /** Whether to show the customer/end-user contact details. Defaults to false. */
  showCustomer?: boolean;
};

export const ConversationPreviewHeader = ({
  csid,
  metaConversation,
  highlights,
  onPreviewClose,
  onGoToConversation,
  showActions = true,
  showDetails = true,
  showCustomer = false,
}: ConversationPreviewHeaderProps): JSX.Element => {
  const conversation = metaConversation.conversation as Conversation;

  return (
    <>
      <FlexBox flexDirection="row">
        <ConversationStatusColorBox
          csid={conversation.id}
          status={conversation.status}
        />

        <Box className={styles.content} flex="1" padding="300 400">
          <ConversationPreviewHeadline
            metaConversation={metaConversation}
            highlights={highlights}
          />

          {showActions && (
            <ConversationPreviewActions
              onGoToConversation={onGoToConversation}
              onClose={onPreviewClose}
              metaConversation={metaConversation}
              csid={csid}
            />
          )}

          {showCustomer && (
            <ConversationPreviewCustomer
              requester={metaConversation.requester}
              showSectionIcon
            />
          )}

          {showDetails && (
            <>
              <ConversationPreviewTags
                tags={metaConversation.conversation.tags ?? []}
                showSectionIcon
              />

              {typeof metaConversation.conversation.id === 'number' && (
                <ConversationPreviewCustomFields
                  csid={metaConversation.conversation.id}
                  showSectionIcon
                />
              )}
            </>
          )}
        </Box>
      </FlexBox>

      <Divider />
    </>
  );
};
